import { omit } from "lodash";

import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";
import {
  InventoryProduct,
  InventoryProductCheckInInput,
  InventoryProductCheckInResponse,
  InventoryProductInput,
  InventoryProductsDownloadQuery,
  InventoryProductsFilters,
  InventoryProductsResponse,
  InventoryProductsResponseOverview,
  NewInventoryProduct,
  SerialNumberHistoryResponse
} from "./types/inventory-products.type";
import { CSVReportResponse } from "./types/reports.type";

export const DEFAULT_INVENTORY_PRODUCTS_LIMIT = 25;

export type ClinicStockCounts = {
  all: number;
  hearing_aids: number;
  accessories: number;
  supplies: number;
  out_of_stock: number;
  loaners: number;
  demos: number;
  // "Stock" quick filter: neither loanable nor demoable.
  stock: number;
};

export const inventoryProductsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getInventoryProducts: builder.query<
      InventoryProduct[],
      InventoryProductsFilters
    >({
      query: ({
        query,
        offset = 0,
        limit = DEFAULT_INVENTORY_PRODUCTS_LIMIT,
        baseFilters,
        ordering = "-checked_in_at,display_name,-id",
        search,
        ...filters
      }) =>
        `/inventory-products/?limit=${limit}&offset=${offset}&${formatValidQueryParamUrlCommaSeparated(
          {
            ...baseFilters,
            ...filters,
            search: search ? encodeURIComponent(search) : undefined,
            // `assigned_tags` is the InventoryProduct's own tags (ENG-1365) —
            // serialized AND non-serialized — so don't derive is_serialized from
            // it, or tag-filtered queries would drop non-serialized items.
            is_serialized: baseFilters?.is_serialized
          }
        )}&query=${query}&ordering=${ordering}`,
      transformResponse: (
        response: InventoryProductsResponse | InventoryProductsResponseOverview
      ) => {
        if ("data" in response) {
          return response.data.results?.map((inventoryProduct) => ({
            ...inventoryProduct,
            overview: response.overview
          }));
        }

        return response.results;
      },
      providesTags: ["InventoryProducts"]
    }),
    getInventoryProductsCount: builder.query<
      number,
      Omit<InventoryProductsFilters, "query" | "limit" | "offset">
    >({
      query: ({ baseFilters, search, ...filters }) =>
        `/inventory-products/count/?${formatValidQueryParamUrlCommaSeparated({
          ...baseFilters,
          ...filters,
          // Encode like the list endpoint above — the formatter doesn't, and an
          // unencoded `&`/`#`/`+` would make count and list filter on different
          // populations, corrupting the picker's at-cap signal.
          search: search ? encodeURIComponent(search) : undefined
        })}`,
      providesTags: ["InventoryProductCounters"]
    }),
    getInventoryProductsCounts: builder.query<
      ClinicStockCounts,
      Omit<InventoryProductsFilters, "query" | "limit" | "offset">
    >({
      query: ({ baseFilters, search, ...filters }) =>
        `/inventory-products/counts/?${formatValidQueryParamUrlCommaSeparated({
          ...baseFilters,
          ...filters,
          // Same encoding as the single-counter endpoint above, so the bulk
          // counters and the list filter on the same population.
          search: search ? encodeURIComponent(search) : undefined
        })}`,
      // Same tag as getInventoryProductsCount so the existing counter
      // invalidations refresh the bulk counts too.
      providesTags: ["InventoryProductCounters"]
    }),
    getInventoryProduct: builder.query<
      InventoryProduct,
      // `query` is required: see InventoryProductsFilters comment. Detail-endpoint
      // callers must declare the projection their consumer reads.
      { inventoryProductId: number; query: string }
    >({
      query: ({ inventoryProductId, query }) =>
        `/inventory-products/${inventoryProductId}/?query=${query}`,
      // Keep "InventoryProducts" so list-wide mutations still refresh the detail;
      // add a detail-only tag so assigned-tag ops can refetch just this cheap
      // single-object query without churning the list or the limit=300 picker.
      providesTags: ["InventoryProducts", "InventoryProductDetail"]
    }),
    addInventoryProduct: builder.mutation<
      NewInventoryProduct,
      InventoryProductInput
    >({
      query: (inventoryProductData) => ({
        url: "/inventory-products/",
        method: "POST",
        body: inventoryProductData
      }),
      invalidatesTags: ["InventoryProducts", "InventoryProductCounters"]
    }),
    editInventoryProduct: builder.mutation<
      InventoryProduct,
      InventoryProductInput
    >({
      query: (inventoryProductData) => ({
        url: `/inventory-products/${inventoryProductData.id}/`,
        method: "PATCH",
        body: omit(inventoryProductData, "id")
      }),
      invalidatesTags: ["InventoryProducts", "SaleItemCosts"]
    }),
    checkInInventoryProducts: builder.mutation<
      InventoryProductCheckInResponse,
      InventoryProductCheckInInput
    >({
      query: (inventoryProductData) => ({
        url: `/inventory-products/check-in`,
        method: "POST",
        body: inventoryProductData
      }),
      invalidatesTags: (_result, _error, arg) =>
        arg.create_order
          ? ["InventoryProducts", "InventoryProductCounters", "Orders"]
          : ["InventoryProducts", "InventoryProductCounters"]
    }),
    downloadInventoryProductsCSV: builder.query<
      CSVReportResponse,
      InventoryProductsDownloadQuery
    >({
      query: ({
        query,
        csvHeader = "*",
        csvLabels,
        baseFilters,
        ordering = "-checked_in_at,display_name,-id",
        ...filters
      }) => ({
        url: `/inventory-products/?query=${query}&csv_header=${csvHeader}&${formatValidQueryParamUrlCommaSeparated(
          {
            csv_labels: csvLabels,
            ...baseFilters,
            ...filters,
            // See list query: assigned_tags isn't serialized-only — don't force is_serialized.
            is_serialized: baseFilters?.is_serialized
          }
        )}&ordering=${ordering}`,
        headers: {
          Accept: "custom-csv/json",
          "X-Reports-DB": "true"
        }
      })
    }),
    postQuickbooksInventorySync: builder.mutation<
      void,
      { inventoryProductId: number }
    >({
      query: ({ inventoryProductId }) => ({
        url: `/inventory-products/${inventoryProductId}/sync/`,
        method: "POST"
      }),
      invalidatesTags: ["InventoryProducts"]
    }),
    getSerialNumberHistory: builder.query<
      SerialNumberHistoryResponse,
      { serialNumber: string }
    >({
      query: ({ serialNumber }) =>
        `/inventory-products/serial-history/?serial_number=${encodeURIComponent(
          serialNumber
        )}`,
      providesTags: (_result, _error, { serialNumber }) => [
        { type: "InventoryProducts", id: `serial-${serialNumber}` }
      ]
    }),
    unlinkPatientLink: builder.mutation<
      { detail: string },
      {
        inventoryProductId: number;
        patientProductId: number;
        serialNumber: string;
      }
    >({
      query: ({ inventoryProductId, patientProductId }) => ({
        url: `/inventory-products/${inventoryProductId}/unlink-patient-link/`,
        method: "POST",
        body: { patient_product_id: patientProductId }
      }),
      invalidatesTags: ["InventoryProducts", "InventoryProductCounters"]
    })
  })
});

export const {
  useGetInventoryProductsQuery,
  useGetInventoryProductQuery,
  useLazyGetInventoryProductQuery,
  useAddInventoryProductMutation,
  useEditInventoryProductMutation,
  useGetInventoryProductsCountQuery,
  useLazyGetInventoryProductsCountQuery,
  useGetInventoryProductsCountsQuery,
  useCheckInInventoryProductsMutation,
  useLazyDownloadInventoryProductsCSVQuery,
  usePostQuickbooksInventorySyncMutation,
  useGetSerialNumberHistoryQuery,
  useUnlinkPatientLinkMutation
} = inventoryProductsApi;
